import React, { useEffect, useState, useRef } from 'react';
import { Award, ChevronDown, ChevronUp, ExternalLink, Search, Filter, Calendar, Clock, MapPin, X, Check, AlertTriangle, Info, ShieldCheck } from 'lucide-react';
import { triggerSubtleConfetti } from '../utils/confetti';
import { trackEvent, EVENTS } from '../utils/posthog';
import { verifiedCertifications, focusOptions, yearOptions, institutionOptions, VerifiedCertification, isValidCertification, certificationPolicy } from '../data/verifiedCertifications';
// Enhanced certification card with more detailed information
const CertificationCard = ({
  certification,
  onExpand
}) => {
  // Validate certification data - skip rendering if invalid
  if (!isValidCertification(certification)) {
    console.warn('Invalid certification data detected:', certification);
    return null;
  }
  const [expanded, setExpanded] = useState(false);
  const cardRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({
    x: 50,
    y: 50
  });
  const [angle, setAngle] = useState(0);
  const handleToggleExpand = e => {
    setExpanded(!expanded);
    if (!expanded) {
      onExpand(certification.id);
      // Track certification expansion
      trackEvent(EVENTS.CERTIFICATION_EXPANDED, {
        certification_id: certification.id,
        certification_title: certification.title,
        focus: certification.focus,
        year: certification.year
      });
    }
    triggerSubtleConfetti(e.currentTarget);
  };
  const handleVerifyCertificate = e => {
    e.preventDefault();
    triggerSubtleConfetti(e.currentTarget);
    // Track verification click
    trackEvent(EVENTS.CERTIFICATION_VERIFIED, {
      certification_id: certification.id,
      certification_title: certification.title,
      institution: certification.institution
    });
    window.open(certification.verifyUrl, '_blank', 'noopener,noreferrer');
  };
  // Liquid glass effect with mouse tracking
  const handleMouseMove = e => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, (e.clientX - rect.left) / rect.width * 100));
    const y = Math.max(0, Math.min(100, (e.clientY - rect.top) / rect.height * 100));
    setMousePosition({
      x,
      y
    });
    // Calculate angle for conic gradient
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const radians = Math.atan2(e.clientY - rect.top - centerY, e.clientX - rect.left - centerX);
    const degrees = (radians * (180 / Math.PI) + 360) % 360;
    setAngle(degrees);
  };
  const handleMouseLeave = () => {
    setMousePosition({
      x: 50,
      y: 50
    });
  };
  const colorClass = certification.featured ? 'bg-coral-pink' : 'bg-sage-mint';
  const shadowClass = expanded ? certification.featured ? 'shadow-glow-coral' : 'shadow-glow-mint' : 'hover:shadow-hover hover:-translate-y-1';
  return <div ref={cardRef} className={`relative bg-white rounded-card shadow-card overflow-hidden transition-all duration-300 ${shadowClass}`} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} style={{
    '--x': `${mousePosition.x}%`,
    '--y': `${mousePosition.y}%`,
    '--angle': `${angle}deg`
  } as React.CSSProperties} data-id="element-20">
      <div className={`h-1 ${colorClass}`} data-id="element-21"></div>
      {/* Liquid glass effect layers */}
      {expanded && <>
          <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
        background: `radial-gradient(circle at var(--x) var(--y), ${certification.featured ? 'rgba(255, 164, 164, 0.8)' : 'rgba(186, 223, 219, 0.8)'} 0%, transparent 70%)`
      }} data-id="element-22" />
          <div className="absolute inset-0 opacity-5 pointer-events-none" style={{
        background: `conic-gradient(from var(--angle), ${certification.featured ? '#FFA4A4, #FFBDBD, #FFA4A4' : '#BADFDB, #D4F0ED, #BADFDB'})`
      }} data-id="element-23" />
        </>}
      <div className="p-6" data-id="element-24">
        {/* Header section with institution and expand button */}
        <div className="flex justify-between items-start" data-id="element-25">
          <div data-id="element-26">
            <div className="inline-flex items-center space-x-2 mb-2" data-id="element-27">
              <div className="w-6 h-6 rounded-full bg-grey-400/20 flex items-center justify-center" data-id="element-28">
                <Award className={`h-3 w-3 ${certification.featured ? 'text-coral-pink' : 'text-sage-mint'}`} data-id="element-29" />
              </div>
              <span className="text-xs text-grey-600" data-id="element-30">
                {certification.institution}
              </span>
            </div>
            <h3 className="text-base font-medium text-charcoal" data-id="element-31">
              {certification.title}
            </h3>
          </div>
          <button onClick={handleToggleExpand} className={`w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 ${expanded ? certification.featured ? 'bg-coral-pink text-white animate-pulse-scale' : 'bg-sage-mint text-white animate-pulse-scale' : 'bg-blush-rose/20 hover:bg-blush-rose/40'}`} data-id="element-32">
            {expanded ? <ChevronUp size={14} data-id="element-33" /> : <ChevronDown size={14} data-id="element-34" />}
          </button>
        </div>
        {/* Basic metadata */}
        <div className="flex flex-wrap items-center mt-2 gap-2" data-id="element-35">
          <span className={`inline-block ${certification.featured ? 'bg-coral-pink/10 text-coral-pink' : 'bg-sage-mint/10 text-sage-mint'} text-xs px-2 py-1 rounded-full transition-all duration-300 cursor-default`} data-id="element-36">
            {certification.focus}
          </span>
          <div className="flex items-center space-x-1 text-xs text-grey-400" data-id="element-37">
            <Clock className="h-3 w-3" data-id="element-38" />
            <span data-id="element-39">{certification.duration}</span>
          </div>
          <div className="flex items-center space-x-1 text-xs text-grey-400" data-id="element-40">
            <Calendar className="h-3 w-3" data-id="element-41" />
            <span data-id="element-42">{certification.displayDate}</span>
          </div>
        </div>
        {/* Expanded content */}
        {expanded && <div className="mt-4 pt-4 border-t border-grey-400/20 animate-fadeIn" data-id="element-43">
            <p className="text-sm text-grey-600 mb-3" data-id="element-44">
              {certification.description}
            </p>
            {/* Singapore relevance */}
            <div className="mb-4 flex items-start" data-id="element-45">
              <div className="flex-shrink-0 mt-0.5" data-id="element-46">
                <MapPin className={`h-4 w-4 ${certification.featured ? 'text-coral-pink' : 'text-sage-mint'} mr-2`} data-id="element-47" />
              </div>
              <p className="text-xs text-grey-600" data-id="element-48">
                <span className="font-medium" data-id="element-49">Relevance to Singapore:</span>{' '}
                {certification.relevanceSG}
              </p>
            </div>
            {/* Verification link */}
            <a href={certification.verifyUrl} className={`inline-flex items-center ${certification.featured ? 'text-coral-pink' : 'text-sage-mint'} text-sm transition-all duration-300 hover:${certification.featured ? 'text-coral-pink/70' : 'text-sage-mint/70'} group`} onClick={handleVerifyCertificate} target="_blank" rel="noopener noreferrer" data-id="element-50">
              Verify at Source
              <ExternalLink className="ml-1 h-3 w-3 group-hover:translate-x-1 transition-transform duration-300" data-id="element-51" />
            </a>
          </div>}
      </div>
      {/* Featured badge */}
      {certification.featured && <div className="absolute top-0 right-0" data-id="element-52">
          <div className="bg-coral-pink text-white text-xs font-medium px-2 py-1 rounded-bl-md" data-id="element-53">
            Featured
          </div>
        </div>}
    </div>;
};
// Search input component
const SearchInput = ({
  value,
  onChange,
  onClear
}) => {
  return <div className="relative" data-id="element-54">
      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none" data-id="element-55">
        <Search className="h-4 w-4 text-grey-400" data-id="element-56" />
      </div>
      <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder="Search certifications..." className="w-full h-10 pl-10 pr-10 rounded-full border border-grey-400/30 focus:border-sage-mint focus:ring-1 focus:ring-sage-mint/30 bg-white/80 backdrop-blur-sm transition-all duration-300" data-id="element-57" />
      {value && <button onClick={onClear} className="absolute inset-y-0 right-3 flex items-center text-grey-400 hover:text-grey-600 transition-colors duration-200" data-id="element-58">
          <X className="h-4 w-4" data-id="element-59" />
        </button>}
    </div>;
};
// Filter dropdown component
const FilterDropdown = ({
  label,
  options,
  value,
  onChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  return <div className="relative" ref={dropdownRef} data-id="element-60">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center justify-between w-full h-10 px-4 rounded-full border border-grey-400/30 bg-white/80 backdrop-blur-sm transition-all duration-300 hover:border-sage-mint/50 text-sm text-grey-600" data-id="element-61">
        <span data-id="element-62">{value || label}</span>
        <Filter className="h-4 w-4 ml-2" data-id="element-63" />
      </button>
      {isOpen && <div className="absolute z-10 mt-1 w-full rounded-lg shadow-lg bg-white/95 backdrop-blur-md border border-grey-400/20 py-1 animate-fadeIn" data-id="element-64">
          {options.map(option => <button key={option} onClick={() => {
        onChange(option.includes('All') ? '' : option);
        setIsOpen(false);
      }} className="flex items-center w-full px-4 py-2 text-sm text-grey-600 hover:bg-sage-mint/10 transition-colors duration-200" data-id="element-65">
              <span className="w-5 flex-shrink-0" data-id="element-66">
                {(option.includes('All') ? !value : option === value) && <Check className="h-4 w-4 text-sage-mint" data-id="element-67" />}
              </span>
              {option}
            </button>)}
        </div>}
    </div>;
};
// Verification badge component
const VerificationBadge = ({
  onClick
}) => {
  return <div className="inline-flex items-center bg-white rounded-full shadow-card px-3 py-1.5 cursor-pointer hover:shadow-glow-mint transition-all duration-300" onClick={onClick} data-id="element-68">
      <ShieldCheck className="h-4 w-4 text-sage-mint mr-2" data-id="element-69" />
      <span className="text-xs font-medium text-charcoal" data-id="element-70">
        Verified Certifications
      </span>
      <Info className="h-3 w-3 text-grey-400 ml-2" data-id="element-71" />
    </div>;
};
// Policy modal component
const PolicyModal = ({
  isOpen,
  onClose
}) => {
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-72">
      <div className="bg-white rounded-lg shadow-lg max-w-xl w-full max-h-[80vh] overflow-auto animate-fadeIn" data-id="element-73">
        <div className="p-6" data-id="element-74">
          <div className="flex justify-between items-center mb-4" data-id="element-75">
            <h3 className="text-lg font-medium text-charcoal flex items-center" data-id="element-76">
              <ShieldCheck className="h-5 w-5 text-sage-mint mr-2" data-id="element-77" />
              Certification Verification Policy
            </h3>
            <button onClick={onClose} className="text-grey-400 hover:text-grey-600 transition-colors duration-200" data-id="element-78">
              <X className="h-5 w-5" data-id="element-79" />
            </button>
          </div>
          <div className="prose prose-sm" data-id="element-80">
            <div className="flex items-start mb-4 bg-sage-mint/10 p-3 rounded-lg" data-id="element-81">
              <AlertTriangle className="h-5 w-5 text-sage-mint mr-2 flex-shrink-0 mt-0.5" data-id="element-82" />
              <p className="text-sm text-grey-600" data-id="element-83">
                This page only displays certifications that have been officially
                earned and verified. Each certification has been validated
                against documentation from the issuing institution.
              </p>
            </div>
            <h4 className="text-sm font-medium text-charcoal" data-id="element-84">
              Verification Process
            </h4>
            <p className="text-sm text-grey-600 mb-3" data-id="element-85">
              Every certification displayed meets these criteria:
            </p>
            <ul className="text-sm text-grey-600 space-y-2 mb-4" data-id="element-86">
              <li className="flex items-start" data-id="element-87">
                <Check className="h-4 w-4 text-sage-mint mr-2 flex-shrink-0 mt-0.5" data-id="element-88" />
                Appears on an official certificate from the listed institution
              </li>
              <li className="flex items-start" data-id="element-89">
                <Check className="h-4 w-4 text-sage-mint mr-2 flex-shrink-0 mt-0.5" data-id="element-90" />
                Includes accurate completion date and duration
              </li>
              <li className="flex items-start" data-id="element-91">
                <Check className="h-4 w-4 text-sage-mint mr-2 flex-shrink-0 mt-0.5" data-id="element-92" />
                Has a valid verification link to the issuing body
              </li>
              <li className="flex items-start" data-id="element-93">
                <Check className="h-4 w-4 text-sage-mint mr-2 flex-shrink-0 mt-0.5" data-id="element-94" />
                Contains accurate translation from Finnish to English
              </li>
            </ul>
            <h4 className="text-sm font-medium text-charcoal" data-id="element-95">
              Verification Links
            </h4>
            <p className="text-sm text-grey-600 mb-4" data-id="element-96">
              Each certification includes a "Verify at Source" link to the
              issuing Finnish institution, primarily the Duodecim Medical
              Society. These links allow direct verification of the
              certification's authenticity.
            </p>
            <div className="border-t border-grey-400/20 pt-4 mt-4" data-id="element-97">
              <p className="text-xs text-grey-600 italic" data-id="element-98">
                For verification inquiries, please contact the issuing
                institution directly through the "Verify at Source" links
                provided with each certification.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
// Main CertificationShowcase component
const CertificationShowcase = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [focusFilter, setFocusFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [institutionFilter, setInstitutionFilter] = useState('');
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);
  // Filter pills for quick filtering
  const quickFilters = ['All', 'Featured', 'Emergency Care', 'Elderly Care', 'Mental Health'];
  // Handle filter click
  const handleFilterClick = filter => {
    setActiveFilter(filter);
    setFocusFilter(filter === 'All' ? '' : filter === 'Featured' ? '' : filter);
    // Track filter usage
    trackEvent(EVENTS.CERTIFICATION_FILTERED, {
      filter_type: 'quick_filter',
      filter_value: filter
    });
    triggerSubtleConfetti(document.getElementById(`filter-${filter}`));
  };
  // Handle card expansion
  const handleCardExpand = id => {
    setExpandedCardId(id);
  };
  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setFocusFilter('');
    setYearFilter('');
    setInstitutionFilter('');
    setActiveFilter('All');
  };
  // Validate certifications
  const validCertifications = verifiedCertifications.filter(cert => isValidCertification(cert));
  // Log any invalid certifications
  useEffect(() => {
    const invalidCount = verifiedCertifications.length - validCertifications.length;
    if (invalidCount > 0) {
      console.warn(`${invalidCount} invalid certifications were excluded from display.`);
    }
  }, [validCertifications.length]);
  // Filter certifications based on all criteria
  const filteredCertifications = validCertifications.filter(cert => {
    // Search query filter
    const matchesSearch = searchQuery === '' || cert.title.toLowerCase().includes(searchQuery.toLowerCase()) || cert.institution.toLowerCase().includes(searchQuery.toLowerCase()) || cert.description.toLowerCase().includes(searchQuery.toLowerCase()) || cert.focus.toLowerCase().includes(searchQuery.toLowerCase()) || cert.relevanceSG.toLowerCase().includes(searchQuery.toLowerCase());
    // Focus/domain filter
    const matchesFocus = focusFilter === '' || cert.focus === focusFilter;
    // Year filter
    const matchesYear = yearFilter === '' || cert.year.toString() === yearFilter;
    // Institution filter
    const matchesInstitution = institutionFilter === '' || cert.institution === institutionFilter;
    // Featured filter (special case)
    const matchesFeatured = activeFilter !== 'Featured' || cert.featured === true;
    return matchesSearch && matchesFocus && matchesYear && matchesInstitution && matchesFeatured;
  });
  // Sort certifications by date (newest first)
  const sortedCertifications = [...filteredCertifications].sort((a, b) => {
    return new Date(b.completedDate).getTime() - new Date(a.completedDate).getTime();
  });
  return <section id="certifications" className="py-section bg-gradient-to-b from-cream-white to-sage-mint/5" data-id="element-99">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-100">
        <div className="flex flex-col items-center mb-8" data-id="element-101">
          {/* SEO optimized H2 */}
          <h2 className="text-2xl md:text-3xl font-light text-charcoal mb-4 text-center" data-id="element-102">
            Patient Safety Certifications & Duodecim Medical Training
          </h2>
          <p className="text-center text-grey-600 mb-4 max-w-3xl" data-id="element-103">
            20+ verified certifications from Finland's Duodecim Medical Society.
            Evidence-based training in geriatric care, emergency nursing, and
            patient safety protocols. Each certification includes official
            verification links.
          </p>
          {/* Verification badge */}
          <VerificationBadge onClick={() => setIsPolicyModalOpen(true)} data-id="element-104" />
        </div>
        {/* Quick Filter Pills */}
        <div className="flex items-center justify-center mb-6 overflow-x-auto pb-2" data-id="element-105">
          <div className="flex space-x-2" data-id="element-106">
            {quickFilters.map(filter => <button id={`filter-${filter}`} key={filter} className={`px-5 py-2 rounded-full text-sm transition-all duration-300 whitespace-nowrap
                  ${activeFilter === filter ? 'bg-sage-mint text-white shadow-glow-mint' : 'bg-white text-grey-600 hover:bg-sage-mint/10 hover:scale-105'}`} onClick={() => handleFilterClick(filter)} data-id="element-107">
                {filter}
              </button>)}
          </div>
        </div>
        {/* Advanced Search and Filters */}
        <div className="mb-8 grid grid-cols-1 md:grid-cols-4 gap-3" data-id="element-108">
          <div className="md:col-span-2" data-id="element-109">
            <SearchInput value={searchQuery} onChange={setSearchQuery} onClear={() => setSearchQuery('')} data-id="element-110" />
          </div>
          <FilterDropdown label="Focus Area" options={focusOptions} value={focusFilter} onChange={setFocusFilter} data-id="element-111" />
          <FilterDropdown label="Year" options={yearOptions} value={yearFilter} onChange={setYearFilter} data-id="element-112" />
        </div>
        {/* Filter status and clear button */}
        {(searchQuery || focusFilter || yearFilter || institutionFilter || activeFilter !== 'All') && <div className="flex items-center justify-between mb-4" data-id="element-113">
            <p className="text-sm text-grey-600" data-id="element-114">
              Showing {sortedCertifications.length} of{' '}
              {validCertifications.length} verified certifications
            </p>
            <button onClick={clearFilters} className="text-sm text-sage-mint hover:text-sage-mint/70 transition-colors duration-200 flex items-center" data-id="element-115">
              <X className="h-3 w-3 mr-1" data-id="element-116" />
              Clear filters
            </button>
          </div>}
        {/* Certification Cards Grid */}
        {sortedCertifications.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-117">
            {sortedCertifications.map(cert => <CertificationCard key={cert.id} certification={cert} onExpand={handleCardExpand} data-id="element-118" />)}
          </div> : <div className="bg-white/80 rounded-lg p-8 text-center shadow-card" data-id="element-119">
            <p className="text-grey-600" data-id="element-120">
              No certifications match your filters.
            </p>
            <button onClick={clearFilters} className="mt-2 text-sage-mint hover:text-sage-mint/70 transition-colors duration-200" data-id="element-121">
              Clear all filters
            </button>
          </div>}
        {/* Verification note */}
        <div className="mt-8 text-center" data-id="element-122">
          <p className="text-xs text-grey-600 max-w-2xl mx-auto" data-id="element-123">
            For verification, each certification includes a "Verify at Source"
            link to the issuing Finnish body (primarily Duodecim Medical
            Society). This page displays only officially earned and verified
            certifications.
          </p>
        </div>
      </div>
      {/* Policy Modal */}
      <PolicyModal isOpen={isPolicyModalOpen} onClose={() => setIsPolicyModalOpen(false)} data-id="element-124" />
    </section>;
};
export default CertificationShowcase;